import { useState } from 'react';
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { getAccounts } from '../../../../api/accountsApi';
import useDebounce from '../../../../hooks/useDebounce';
import STRING_KEYS from '../../../../language/keys';
import { Account } from '../../../../@types/Account';
import style from '../style';

const MIN_SEARCH_LENGTH = 2;
const SEARCH_LIMIT = 10;
const ACCOUNT_SELECT = ['id', 'displayName', 'subDomainName', 'allowedEmailDomain'];

type LinkAccountDialogProps = {
  isOpen: boolean,
  isSubmitting: boolean,
  companyName: string,
  onConfirm: (accountId: string) => void,
  onClose: () => void,
};

const getAccountLabel = (account: Account): string => {
  const details = [account.subDomainName, account.allowedEmailDomain].filter(Boolean).join(' · ');

  return details ? `${account.displayName} (${details})` : account.displayName;
};

export const LinkAccountDialog = ({
  isOpen,
  isSubmitting,
  companyName,
  onConfirm,
  onClose,
}: LinkAccountDialogProps): JSX.Element => {
  const { t: translate } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const { debouncedValue } = useDebounce(searchTerm.trim());

  const { data, isFetching } = useQuery(
    ['referral-link-accounts', debouncedValue],
    () => getAccounts({
      select: ACCOUNT_SELECT,
      contains: { displayName: debouncedValue },
      limit: SEARCH_LIMIT,
    }),
    { enabled: debouncedValue.length >= MIN_SEARCH_LENGTH },
  );

  return (
    <Dialog open={ isOpen } onClose={ onClose } fullWidth>
      <DialogTitle>{ translate(STRING_KEYS.REFERRALS.LINK_DIALOG_TITLE) }</DialogTitle>
      <DialogContent>
        <DialogContentText>
          { translate(STRING_KEYS.REFERRALS.LINK_DIALOG_SUB_TITLE, { company: companyName }) }
        </DialogContentText>
        <Autocomplete
          options={ data?.results || [] }
          value={ selectedAccount }
          loading={ isFetching }
          filterOptions={ (options) => options }
          getOptionLabel={ getAccountLabel }
          isOptionEqualToValue={ (option, value) => option.id === value.id }
          onChange={ (e, account) => setSelectedAccount(account) }
          onInputChange={ (e, value, reason) => {
            // Picking an option also fires an input change with its label; only search on typing.
            if (reason === 'input') {
              setSearchTerm(value);
            }
          } }
          sx={ style.field.sx }
          renderInput={ (params) => (
            <TextField
              { ...params }
              autoFocus
              label={ translate(STRING_KEYS.REFERRALS.LINK_DIALOG_SEARCH) }
              InputProps={ {
                ...params.InputProps,
                endAdornment: (
                  <>
                    { isFetching && <CircularProgress size={ 20 } /> }
                    { params.InputProps.endAdornment }
                  </>
                ),
              } }
            />
          ) }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={ onClose }>{ translate(STRING_KEYS.DIALOG.BTN_GO_BACK) }</Button>
        <Button
          variant="contained"
          disabled={ !selectedAccount || isSubmitting }
          onClick={ () => selectedAccount && onConfirm(selectedAccount.id) }
        >
          { translate(STRING_KEYS.REFERRALS.LINK_DIALOG_BTN) }
        </Button>
      </DialogActions>
    </Dialog>
  );
};
